import { HStack, Text, Avatar, Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { REACTION_EMOJIS, type ReactionType, type ReactionCounts, type ReactedByUser } from '@/types/social';

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

interface ReactionDisplayProps {
  totalCount: number;
  counts: ReactionCounts;
  recentUsers: ReactedByUser[];
  userReaction?: ReactionType | null;
  onClick?: () => void;
}

export const ReactionDisplay = ({
  totalCount,
  counts,
  recentUsers,
  userReaction,
  onClick,
}: ReactionDisplayProps) => {
  if (totalCount === 0) {
    return null;
  }

  // Get top 3 reaction types by count
  const sortedReactions = (Object.entries(counts) as [ReactionType, number][])
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Build display text
  const getDisplayText = () => {
    if (userReaction && totalCount === 1) {
      return 'You reacted';
    }
    if (userReaction && totalCount === 2) {
      return 'You and 1 other';
    }
    if (userReaction && totalCount > 2) {
      return `You and ${totalCount - 1} others`;
    }
    if (totalCount === 1 && recentUsers.length > 0) {
      const name = recentUsers[0]?.user?.firstName || recentUsers[0]?.user?.lastName || 'Someone';
      return `${name} reacted`;
    }
    return `${totalCount} reactions`;
  };

  return (
    <HStack
      spacing={2}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      _hover={onClick ? { opacity: 0.8 } : undefined}
      role={onClick ? 'button' : undefined}
    >
      {/* Emoji Stack */}
      <HStack spacing={-1}>
        {sortedReactions.map(([reaction], index) => (
          <Box
            key={reaction}
            bg="var(--card-bg)"
            borderRadius="full"
            border="2px solid var(--card-bg)"
            boxShadow="0 1px 3px var(--color-overlay-10)"
            w="22px"
            h="22px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="sm"
            zIndex={3 - index}
            animation={userReaction === reaction ? `${pulseAnimation} 0.5s ease-in-out` : undefined}
          >
            {REACTION_EMOJIS[reaction]}
          </Box>
        ))}
      </HStack>

      {/* Avatar Stack (if users available) */}
      {recentUsers.length > 0 && (
        <HStack spacing={-2}>
          {recentUsers.slice(0, 3).map((item, index) => (
            <Avatar
              key={item.user.id}
              size="xs"
              name={`${item.user.firstName || ''} ${item.user.lastName || ''}`.trim() || undefined}
              src={item.user.photoUrl || undefined}
              border="2px solid"
              borderColor="var(--card-bg)"
              zIndex={3 - index}
            />
          ))}
        </HStack>
      )}

      {/* Count Text */}
      <Text fontSize="sm" color="var(--card-text-2)">
        {getDisplayText()}
      </Text>
    </HStack>
  );
};
